import { FC, useMemo, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { RequestsFormValues } from '../../models';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { useReferences } from '../../../../../helpers/context_manager/useReferences';
import { toOptions } from '../../../../../helpers/toOptions';
import { FieldError } from './FieldError';

type BoolOrganKey = keyof Omit<
  RequestsFormValues['histology']['form_data']['histology_fixed'],
  'cns' | 'bone_marrow' | 'other'
>;

const BOOLEAN_ORGANS: { key: BoolOrganKey; label: string }[] = [
  { key: 'nasal_septum', label: 'nosní přepážka' },
  { key: 'epiglottis', label: 'epiglottis' },
  { key: 'larynx', label: 'hrtan' },
  { key: 'thyroid_gland', label: 'štítná žláza' },
  { key: 'trachea', label: 'průdušnice' },
  { key: 'lungs', label: 'plíce' },
  { key: 'heart', label: 'srdce' },
  { key: 'thymus', label: 'thymus' },
  { key: 'liver', label: 'játra' },
  { key: 'spleen', label: 'slezina' },
  { key: 'stomach', label: 'žaludek' },
  { key: 'small_intestine', label: 'tenké střevo' },
  { key: 'large_intestine_colon', label: 'tlusté střevo' },
  { key: 'pancreas', label: 'pankreas' },
  { key: 'adrenal_glands', label: 'nadledviny' },
  { key: 'kidneys', label: 'ledviny' },
  { key: 'prostate', label: 'prostata' },
  { key: 'eyeballs_and_optic_nerves', label: 'bulby a zrakové nervy' },
  { key: 'pituitary_gland', label: 'hypofýza' }
];

const CNS_CHOICES = [
  { value: 'brain', label: 'mozek' },
  { value: 'cerebellum', label: 'mozeček' },
  { value: 'brainstem', label: 'mozkový kmen' },
  { value: 'brain_dura_mater', label: 'tvrdá plena' },
  { value: 'spinal_cord', label: 'mícha' }
] as const;

const BONE_MARROW_CHOICES = [
  { value: 'vertebra', label: 'obratel' },
  { value: 'sternum', label: 'sternum' },
  { value: 'rib', label: 'žebro' },
  { value: 'femur', label: 'femur' }
] as const;

const fd = (path: string) => `histology.form_data.${path}` as const;

export const HistologyForm: FC = () => {
  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors }
  } = useFormContext<RequestsFormValues>();
  const { references, loading } = useReferences();
  const personsOptions = useMemo(() => toOptions(references.persons), [references.persons]);

  const cnsEnabled = watch('histology.form_data.histology_fixed.cns.enabled' as any);
  const boneMarrowEnabled = watch('histology.form_data.histology_fixed.bone_marrow.enabled' as any);
  type OtherItem = { enabled: boolean; text: string | null };
  const histologyOther = (watch('histology.form_data.histology_fixed.other' as any) as OtherItem[]) || [];
  const cytologyOther = (watch('histology.form_data.cytology.other' as any) as OtherItem[]) || [];

  const [newHistologyOther, setNewHistologyOther] = useState('');
  const [newCytologyOther, setNewCytologyOther] = useState('');

  const addHistologyOther = () => {
    const trimmed = newHistologyOther.trim();
    if (trimmed) {
      setValue('histology.form_data.histology_fixed.other' as any, [...histologyOther, { enabled: true, text: trimmed }]);
      setNewHistologyOther('');
    }
  };
  const removeHistologyOther = (idx: number) =>
    setValue('histology.form_data.histology_fixed.other' as any, histologyOther.filter((_, i) => i !== idx));

  const addCytologyOther = () => {
    const trimmed = newCytologyOther.trim();
    if (trimmed) {
      setValue('histology.form_data.cytology.other' as any, [...cytologyOther, { enabled: true, text: trimmed }]);
      setNewCytologyOther('');
    }
  };
  const removeCytologyOther = (idx: number) =>
    setValue('histology.form_data.cytology.other' as any, cytologyOther.filter((_, i) => i !== idx));

  const {
    fields: specialRows,
    append: appendSpecial,
    remove: removeSpecial
  } = useFieldArray({ control, name: 'histology.form_data.special_staining' as any });

  const {
    fields: frozenRows,
    append: appendFrozen,
    remove: removeFrozen
  } = useFieldArray({ control, name: 'histology.form_data.frozen_material' as any });

  return (
    <Wrapper>
      {/* Indikující lékař */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Indikující lékař *</SectionLabel>
          </Col>
          <Col md={9}>
            <Select
              disabled={loading}
              {...register('histology.requesting_physician', {
                required: 'Indikující lékař je povinný'
              })}>
              <option value="">{loading ? 'Načítám...' : 'Zvolte lékaře'}</option>
              {personsOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <FieldError error={(errors as any)?.histology?.requesting_physician} />
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Makroskopická histochemie srdce */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Histochemie srdce</SectionLabel>
          </Col>
          <Col md={9}>
            <Check>
              <input type="checkbox" {...register('histology.macroscopic_heart_histochemistry')} />
              <span>Makroskopická histochemie srdce</span>
            </Check>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Fixovaný materiál */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Fixovaný materiál</SectionLabel>
          </Col>
          <Col md={9}>
            <OrganGrid>
              {/* CNS */}
              <Check>
                <input type="checkbox" {...register(fd('histology_fixed.cns.enabled') as any)} />
                <span>CNS</span>
              </Check>
              {cnsEnabled && (
                <>
                  <SubChoices>
                    {CNS_CHOICES.map((c) => (
                      <Check key={c.value}>
                        <input
                          type="checkbox"
                          value={c.value}
                          {...register(fd('histology_fixed.cns.choices') as any, {
                            validate: (choices: string[]) =>
                              !cnsEnabled || choices?.length > 0 || 'Vyberte aspoň jednu možnosť'
                          })}
                        />
                        <span>{c.label}</span>
                      </Check>
                    ))}
                  </SubChoices>
                  <FieldError
                    error={(errors as any)?.histology?.form_data?.histology_fixed?.cns?.choices}
                  />
                </>
              )}

              {/* Boolean organs */}
              {BOOLEAN_ORGANS.map((o) => (
                <Check key={o.key}>
                  <input type="checkbox" {...register(fd(`histology_fixed.${o.key}`) as any)} />
                  <span>{o.label}</span>
                </Check>
              ))}

              {/* Bone marrow */}
              <Check>
                <input
                  type="checkbox"
                  {...register(fd('histology_fixed.bone_marrow.enabled') as any)}
                />
                <span>kostní dřeň</span>
              </Check>
              {boneMarrowEnabled && (
                <>
                  <SubChoices>
                    {BONE_MARROW_CHOICES.map((c) => (
                      <Check key={c.value}>
                        <input
                          type="checkbox"
                          value={c.value}
                          {...register(fd('histology_fixed.bone_marrow.choices') as any, {
                            validate: (choices: string[]) =>
                              !boneMarrowEnabled ||
                              choices?.length > 0 ||
                              'Vyberte aspoň jednu možnosť'
                          })}
                        />
                        <span>{c.label}</span>
                      </Check>
                    ))}
                  </SubChoices>
                  <FieldError
                    error={
                      (errors as any)?.histology?.form_data?.histology_fixed?.bone_marrow?.choices
                    }
                  />
                </>
              )}

              {/* Jiné — histology_fixed */}
              <OtherList>
                <OtherAddRow>
                  <OtherInput
                    type="text"
                    placeholder="jiné..."
                    value={newHistologyOther}
                    onChange={(e) => setNewHistologyOther(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHistologyOther(); } }}
                  />
                  <AddBtn type="button" onClick={addHistologyOther}>+ přidat</AddBtn>
                </OtherAddRow>
                {histologyOther.map((item, idx) => (
                  <OtherTag key={idx}>
                    <span>{item.text}</span>
                    <RemoveBtn type="button" onClick={() => removeHistologyOther(idx)}>✕</RemoveBtn>
                  </OtherTag>
                ))}
              </OtherList>
            </OrganGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Speciální barvení */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Speciální barvení</SectionLabel>
          </Col>
          <Col md={9}>
            <DynamicRows>
              {specialRows.length > 0 && (
                <RowHead>
                  <span>Tkáň</span>
                  <span>Metoda</span>
                </RowHead>
              )}
              {(specialRows as any[]).map((field, idx) => (
                <DynamicRow key={field.id}>
                  <InputCell>
                    <TissueInput
                      type="text"
                      placeholder="tkáň"
                      {...register(`histology.form_data.special_staining.${idx}.tissue` as any, {
                        validate: (v: string) => !!v?.trim() || 'Povinné pole'
                      })}
                    />
                    <FieldError
                      error={(errors as any)?.histology?.form_data?.special_staining?.[idx]?.tissue}
                    />
                  </InputCell>
                  <InputCell>
                    <TissueInput
                      type="text"
                      placeholder="metoda"
                      {...register(`histology.form_data.special_staining.${idx}.method` as any, {
                        validate: (v: string) => !!v?.trim() || 'Povinné pole'
                      })}
                    />
                    <FieldError
                      error={(errors as any)?.histology?.form_data?.special_staining?.[idx]?.method}
                    />
                  </InputCell>
                  <RemoveBtn type="button" onClick={() => removeSpecial(idx)}>
                    ✕
                  </RemoveBtn>
                </DynamicRow>
              ))}
              <AddBtn
                type="button"
                onClick={() => appendSpecial({ tissue: '', method: '' } as any)}>
                + přidat řádek
              </AddBtn>
            </DynamicRows>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Zmrazený materiál */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Zmrazený materiál</SectionLabel>
          </Col>
          <Col md={9}>
            <DynamicRows>
              {frozenRows.length > 0 && (
                <RowHead>
                  <span>Tkáň</span>
                  <span>Metoda</span>
                </RowHead>
              )}
              {(frozenRows as any[]).map((field, idx) => (
                <DynamicRow key={field.id}>
                  <InputCell>
                    <TissueInput
                      type="text"
                      placeholder="tkáň"
                      {...register(`histology.form_data.frozen_material.${idx}.tissue` as any, {
                        validate: (v: string) => !!v?.trim() || 'Povinné pole'
                      })}
                    />
                    <FieldError
                      error={(errors as any)?.histology?.form_data?.frozen_material?.[idx]?.tissue}
                    />
                  </InputCell>
                  <InputCell>
                    <TissueInput
                      type="text"
                      placeholder="metoda"
                      {...register(`histology.form_data.frozen_material.${idx}.method` as any, {
                        validate: (v: string) => !!v?.trim() || 'Povinné pole'
                      })}
                    />
                    <FieldError
                      error={(errors as any)?.histology?.form_data?.frozen_material?.[idx]?.method}
                    />
                  </InputCell>
                  <RemoveBtn type="button" onClick={() => removeFrozen(idx)}>
                    ✕
                  </RemoveBtn>
                </DynamicRow>
              ))}
              <AddBtn type="button" onClick={() => appendFrozen({ tissue: '', method: '' } as any)}>
                + přidat řádek
              </AddBtn>
            </DynamicRows>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Poznámky */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Poznámky</SectionLabel>
          </Col>
          <Col md={9}>
            <NotesGrid>
              <NoteRow>
                <NoteLabel>Pitvající lékař</NoteLabel>
                <NoteInput
                  type="text"
                  {...register(fd('histology_notes.sectioning_physician.text') as any)}
                />
              </NoteRow>
              <NoteRow>
                <NoteLabel>Fixace celého orgánu</NoteLabel>
                <NoteInput
                  type="text"
                  {...register(fd('histology_notes.whole_organ_fixation') as any)}
                />
              </NoteRow>
              <NoteRow>
                <NoteLabel>Ostatní</NoteLabel>
                <NoteInput type="text" {...register(fd('histology_notes.other') as any)} />
              </NoteRow>
            </NotesGrid>
          </Col>
        </Row>
      </Section>

      <Divider />

      {/* Cytologie */}
      <Section>
        <Row>
          <Col md={3}>
            <SectionLabel>Cytologie</SectionLabel>
          </Col>
          <Col md={9}>
            <OrganGrid>
              <SideChoiceRow
                label="Nosní dutina"
                basePath={fd('cytology.nasal_cavity')}
                register={register}
                watch={watch}
                error={(errors as any)?.histology?.form_data?.cytology?.nasal_cavity?.choices}
              />
              <Check>
                <input type="checkbox" {...register(fd('cytology.oral_cavity') as any)} />
                <span>Dutina ústní</span>
              </Check>
              <Check>
                <input type="checkbox" {...register(fd('cytology.vulva') as any)} />
                <span>Vulva</span>
              </Check>
              <Check>
                <input type="checkbox" {...register(fd('cytology.vagina') as any)} />
                <span>Vagína</span>
              </Check>

              {/* Uterus */}
              <UterusRow
                register={register}
                watch={watch}
                basePath={fd('cytology.uterus')}
                error={(errors as any)?.histology?.form_data?.cytology?.uterus?.choices}
              />

              <Check>
                <input type="checkbox" {...register(fd('cytology.rectum') as any)} />
                <span>Rektum</span>
              </Check>
              <SideChoiceRow
                label="Sklivec"
                basePath={fd('cytology.vitreous')}
                register={register}
                watch={watch}
                error={(errors as any)?.histology?.form_data?.cytology?.vitreous?.choices}
              />

              {/* Jiné — cytology */}
              <OtherList>
                <OtherAddRow>
                  <OtherInput
                    type="text"
                    placeholder="jiné..."
                    value={newCytologyOther}
                    onChange={(e) => setNewCytologyOther(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCytologyOther(); } }}
                  />
                  <AddBtn type="button" onClick={addCytologyOther}>+ přidat</AddBtn>
                </OtherAddRow>
                {cytologyOther.map((item, idx) => (
                  <OtherTag key={idx}>
                    <span>{item.text}</span>
                    <RemoveBtn type="button" onClick={() => removeCytologyOther(idx)}>✕</RemoveBtn>
                  </OtherTag>
                ))}
              </OtherList>

              <NoteRow>
                <NoteLabel>Poznámka</NoteLabel>
                <NoteInput type="text" {...register(fd('cytology.note') as any)} />
              </NoteRow>
            </OrganGrid>
          </Col>
        </Row>
      </Section>
    </Wrapper>
  );
};

const SideChoiceRow: FC<{
  label: string;
  basePath: string;
  register: any;
  watch: any;
  error?: any;
}> = ({ label, basePath, register, watch, error }) => {
  const enabled = watch(`${basePath}.enabled`);
  return (
    <>
      <Check>
        <input type="checkbox" {...register(`${basePath}.enabled`)} />
        <span>{label}</span>
      </Check>
      {enabled && (
        <>
          <SubChoices>
            <Check>
              <input
                type="checkbox"
                value="right"
                {...register(`${basePath}.choices`, {
                  validate: (choices: string[]) =>
                    !enabled || choices?.length > 0 || 'Vyberte aspoň jednu možnosť'
                })}
              />
              <span>vpravo</span>
            </Check>
            <Check>
              <input type="checkbox" value="left" {...register(`${basePath}.choices`)} />
              <span>vlevo</span>
            </Check>
          </SubChoices>
          <FieldError error={error} />
        </>
      )}
    </>
  );
};

const UterusRow: FC<{ register: any; watch: any; basePath: string; error?: any }> = ({
  register,
  watch,
  basePath,
  error
}) => {
  const enabled = watch(`${basePath}.enabled`);
  return (
    <>
      <Check>
        <input type="checkbox" {...register(`${basePath}.enabled`)} />
        <span>Děloha</span>
      </Check>
      {enabled && (
        <>
          <SubChoices>
            <Check>
              <input
                type="checkbox"
                value="cervix"
                {...register(`${basePath}.choices`, {
                  validate: (choices: string[]) =>
                    !enabled || choices?.length > 0 || 'Vyberte aspoň jednu možnosť'
                })}
              />
              <span>hrdlo (cervix)</span>
            </Check>
            <Check>
              <input type="checkbox" value="cavity" {...register(`${basePath}.choices`)} />
              <span>dutina</span>
            </Check>
          </SubChoices>
          <FieldError error={error} />
        </>
      )}
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: ${Spacing(3)} 0;
`;

const SectionLabel = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: 0;
`;

const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  user-select: none;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  span {
    color: ${Colors.grey};
  }
`;

const OrganGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(2)};
`;

const SubChoices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing(2)};
  padding-left: ${Spacing(4)};
`;

const Select = styled.select`
  height: 34px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
  max-width: 400px;
`;

const DynamicRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(2)};
`;

const RowHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: ${Spacing(2)};

  span {
    color: ${Colors.grey};
    font-weight: 700;
  }
`;

const DynamicRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: ${Spacing(2)};
  align-items: start;
`;

const InputCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TissueInput = styled.input`
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
`;

const RemoveBtn = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  background: none;
  cursor: pointer;
  color: ${Colors.grey};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${Colors.lightGrey};
  }
`;

const AddBtn = styled.button`
  align-self: flex-start;
  padding: ${Spacing(1)} ${Spacing(2)};
  border: 1px dashed ${Colors.lightGrey};
  background: none;
  cursor: pointer;
  color: ${Colors.grey};
  font-size: 13px;

  &:hover {
    background: ${Colors.lightGrey};
  }
`;

const NotesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(3)};
`;

const NoteRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: ${Spacing(2)};
  align-items: center;
`;

const NoteLabel = styled.div`
  color: ${Colors.grey};
`;

const NoteInput = styled.input`
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
`;

const OtherList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
`;

const OtherAddRow = styled.div`
  display: flex;
  gap: ${Spacing(2)};
  align-items: center;
`;

const OtherInput = styled.input`
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 200px;
`;

const OtherTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing(2)};
  padding: ${Spacing(1)} ${Spacing(2)};
  border: 1px solid ${Colors.lightGrey};
  background: ${Colors.lightGrey}40;
  align-self: flex-start;

  span {
    color: ${Colors.grey};
    font-size: 13px;
  }
`;
